import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, PROFILE_KEY } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePlatform } from "@/lib/platform";

const ProfileContext = createContext(null);

export function ProfileProvider({ children }) {
  const { user } = useAuth();
  const maintenance = usePlatform().maintenance === true && !["admin", "owner"].includes(user?.role);
  const [profiles, setProfiles] = useState(null);
  const [activeId, setActiveId] = useState(() => localStorage.getItem(PROFILE_KEY));
  const [myList, setMyList] = useState(new Set());

  const loadProfiles = useCallback(async () => {
    const { data } = await api.get("/profiles");
    setProfiles(data);
    return data;
  }, []);

  useEffect(() => {
    if (!user) return setProfiles(null);
    if (maintenance) return undefined;  // retried automatically once maintenance clears
    // Keep the current selection on transient errors (503/network); only an empty list from the server clears it.
    loadProfiles().catch((e) => { if (e.response?.status === 401 || e.response?.status === 403) setProfiles([]); });
  }, [user, loadProfiles, maintenance]);

  const activeProfile = profiles?.find((p) => p.id === activeId) || null;

  useEffect(() => {
    if (profiles && activeId && !profiles.some((p) => p.id === activeId)) {
      localStorage.removeItem(PROFILE_KEY);
      setActiveId(null);
    }
  }, [profiles, activeId]);

  useEffect(() => {
    if (!activeProfile) return setMyList(new Set());
    api.get("/library/list/ids").then(({ data }) => setMyList(new Set(data))).catch(() => {});
  }, [activeProfile]);

  const selectProfile = (id) => {
    localStorage.setItem(PROFILE_KEY, id);
    setActiveId(id);
  };

  const toggleMyList = async (slug, title) => {
    const inList = myList.has(slug);
    try {
      if (inList) await api.delete(`/library/list/${slug}`);
      else await api.post(`/library/list/${slug}`);
      setMyList((prev) => {
        const next = new Set(prev);
        inList ? next.delete(slug) : next.add(slug);
        return next;
      });
      toast.success(inList ? `Removed ${title || "title"} from My List` : `Added ${title || "title"} to My List`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <ProfileContext.Provider value={{ profiles, activeProfile, selectProfile, loadProfiles, myList, toggleMyList }}>
      {children}
    </ProfileContext.Provider>
  );
}

export const useProfiles = () => useContext(ProfileContext);
